// Typed calls for the catalogue screens (Sprint 4): the routes of packages/contracts/src/openapi/inventory.ts §5.10 — products,
// their barcodes, the scan lookup and the brands, a lens lab's component options, the service-combination rules and the
// organisation's catalogue settings — plus the two reads the product form leans on: the platform's tariff codes and the effective
// settings (whether a new frame starts serialised). Response schemas are the contract's own (TEN-73).
import {
  CatalogueSettingsViewSchema, EffectiveSettingsSchema, LensComponentOptionPageSchema, LensComponentOptionViewSchema, ProductBarcodeViewSchema,
  ProductBrandsResultSchema, ProductLookupResultSchema, ProductPageSchema, ProductViewSchema, ProductVoidResultSchema, ProductWriteResultSchema,
  SaoaCodeViewSchema, ServiceCombinationRulePageSchema, ServiceCombinationRuleViewSchema,
  type BarcodeKind, type CatalogueSettingsPatch, type LensComponentOptionKind, type ProductCategory,
} from "@visionx/contracts";
import { request } from "./client";
import { PageSchema, type Cursor, type Grant } from "./endpoints";

/** The list's query (ProductListQuerySchema) as the toolbar holds it; `category` is one or several, comma-separated. */
export interface ProductFilters {
  q?: string | undefined;
  category?: readonly ProductCategory[] | undefined;
  supplierId?: string | undefined;
  brand?: string | undefined;
  includeInactive?: boolean | undefined;
  notInLatestPriceList?: boolean | undefined;
}

/**
 * A product's body as the form sends it. The contract's schemas parse `unknown` fields into their own codes (`required`,
 * `out_of_range`…), so the body is typed here by what the form puts in it; the server is the judge.
 */
export type ProductBody = Record<string, unknown>;
export type OptionBody = Record<string, unknown>;
export type RuleBody = Record<string, unknown>;

const flag = (v: boolean | undefined): "true" | undefined => (v ? "true" : undefined);

export const products = {
  /** UC-CATALOGUE-003: a page in the tabs' order, the acting branch's figures beside each row; `counts` answers each tab's count. */
  list: (f: ProductFilters, c: Cursor & { count?: boolean | undefined } = {}) => request("/products", {
    query: {
      q: f.q, category: f.category && f.category.length > 0 ? f.category.join(",") : undefined, supplierId: f.supplierId, brand: f.brand,
      includeInactive: flag(f.includeInactive), notInLatestPriceList: flag(f.notInLatestPriceList), count: flag(c.count), limit: c.limit ?? 50, cursor: c.cursor,
    },
    schema: ProductPageSchema,
  }),
  /** UC-CATALOGUE-003 A2: the organisation's brands, narrowing by the starts of their words. */
  brands: (q: string) => request("/products/brands", { query: { q }, schema: ProductBrandsResultSchema }),
  /** UC-CATALOGUE-004: a scanned or typed code, resolved as a serial, a barcode, an SKU, then a tag. */
  lookup: (code: string, category?: readonly ProductCategory[] | undefined) => request("/products/lookup", { query: { code, category: category?.join(",") }, schema: ProductLookupResultSchema }),
  get: (id: string) => request(`/products/${id}`, { schema: ProductViewSchema }),
  create: (body: ProductBody, idempotencyKey: string) => request("/products", { method: "POST", body, idempotencyKey, schema: ProductWriteResultSchema }),
  /** UC-CATALOGUE-002; a price lowered or cleared past the threshold needs a Control PIN (`PriceOverride`, the product; UC-PRICING-001 A6). */
  patch: (id: string, body: ProductBody, ifMatch: string, g: Grant = {}) => request(`/products/${id}`, { method: "PATCH", body, ifMatch, stepUpToken: g.token, schema: ProductWriteResultSchema }),
  /** UC-CATALOGUE-005: naturally idempotent; a component of an active composite answers the warning naming it. */
  deactivate: (id: string) => request(`/products/${id}/deactivate`, { method: "POST", body: {}, schema: ProductWriteResultSchema }),
  activate: (id: string) => request(`/products/${id}/activate`, { method: "POST", body: {}, schema: ProductWriteResultSchema }),
  /** UC-CATALOGUE-006: versioned and keyed, a Control PIN (`Delete`); refused 409 `has_history` before any PIN. */
  void: (id: string, reason: string, ifMatch: string, g: Grant) =>
    request(`/products/${id}/void`, { method: "POST", body: { reason }, ifMatch, stepUpToken: g.token, idempotencyKey: g.idempotencyKey, schema: ProductVoidResultSchema }),
  /** UC-CATALOGUE-007: `{ kind, value }`, or `{ kind: "Generated" }` for the practice's own EAN-13. */
  addBarcode: (id: string, body: { kind: BarcodeKind; value?: string | undefined }, idempotencyKey: string) =>
    request(`/products/${id}/barcodes`, { method: "POST", body, idempotencyKey, schema: ProductBarcodeViewSchema }),
  removeBarcode: (id: string, barcodeId: string, idempotencyKey: string) => request(`/products/${id}/barcodes/${barcodeId}`, { method: "DELETE", idempotencyKey }),
};

export interface OptionFilters { supplierId?: string | undefined; kind?: LensComponentOptionKind | undefined; includeInactive?: boolean | undefined }

/** UC-CATALOGUE-009: a lens lab's coatings, tints and PPF. Read by every role; written by the stock roles. */
export const lensOptions = {
  list: (f: OptionFilters, c: Cursor = {}) => request("/lens-component-options", {
    query: { supplierId: f.supplierId, kind: f.kind, includeInactive: flag(f.includeInactive), limit: c.limit ?? 200, cursor: c.cursor }, schema: LensComponentOptionPageSchema,
  }),
  create: (body: OptionBody, idempotencyKey: string) => request("/lens-component-options", { method: "POST", body, idempotencyKey, schema: LensComponentOptionViewSchema }),
  patch: (id: string, body: OptionBody, ifMatch: string) => request(`/lens-component-options/${id}`, { method: "PATCH", body, ifMatch, schema: LensComponentOptionViewSchema }),
};

/** UC-CATALOGUE-010: Owner and Admin (`catalogue.rules.write`), reading included; inactive rules are listed as such. */
export const serviceRules = {
  list: (c: Cursor = {}) => request("/service-combination-rules", { query: { limit: c.limit ?? 200, cursor: c.cursor }, schema: ServiceCombinationRulePageSchema }),
  create: (body: RuleBody, idempotencyKey: string) => request("/service-combination-rules", { method: "POST", body, idempotencyKey, schema: ServiceCombinationRuleViewSchema }),
  patch: (id: string, body: RuleBody, ifMatch: string) => request(`/service-combination-rules/${id}`, { method: "PATCH", body, ifMatch, schema: ServiceCombinationRuleViewSchema }),
};

/** UC-CATALOGUE-011: the organisation's values of the catalogue, pricing and stock settings (`catalogue.settings.write`). */
export const catalogueSettings = {
  get: () => request("/catalogue-settings", { schema: CatalogueSettingsViewSchema }),
  patch: (body: CatalogueSettingsPatch) => request("/catalogue-settings", { method: "PATCH", body, schema: CatalogueSettingsViewSchema }),
};

/** The platform's tariff codes (UC-PLATFORM-REFERENCE-006) for the product form's tariff picker: a code prefix or a description word. */
export const tariffCodes = {
  search: (q: string) => request("/reference/saoa-codes", { query: { q, limit: 10 }, schema: PageSchema(SaoaCodeViewSchema) }),
};

/** The settings in force at the acting branch, every role (UC-TENANCY-007): the form reads whether a new frame starts serialised. */
export const effectiveSettings = {
  get: () => request("/settings/effective", { schema: EffectiveSettingsSchema.partial({ patientTitles: true }) }),
};
